/**
 * Requires a meat product (beef box or individual cut) in the cart before
 * a Foraged Tote Bag can be added. Removes orphan totes if meat is removed.
 */
(function () {
  const SOURCE = 'tote-meat-prerequisite';

  let syncing = false;
  let queued = false;
  let cachedCart = null;

  function getConfig() {
    return window.toteCartRules || null;
  }

  function isEnabled(config) {
    return Boolean(config?.enabled && config.toteProductId && (config.meatProductIds || []).length);
  }

  function idInList(id, list) {
    return list.includes(Number(id)) || list.includes(String(id));
  }

  function isToteItem(item, config) {
    return idInList(item.product_id, [config.toteProductId]);
  }

  function isMeatItem(item, config) {
    return idInList(item.product_id, config.meatProductIds || []);
  }

  function isToteVariantId(variantId, config) {
    const ids = config.toteVariantIds || [];
    return idInList(variantId, ids);
  }

  function cartHasMeat(cart, config) {
    return (cart.items || []).some((item) => isMeatItem(item, config));
  }

  function prerequisiteMessage(config) {
    return (
      config.prerequisiteMessage ||
      'Add a beef box or meat cut to your cart before adding a Foraged Tote Bag.'
    );
  }

  async function fetchCart(force = false) {
    if (!force && cachedCart) return cachedCart;
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch cart');
    cachedCart = await response.json();
    return cachedCart;
  }

  function invalidateCartCache() {
    cachedCart = null;
  }

  async function changeLineQuantity(lineKey, quantity) {
    const response = await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
    const data = await response.json();
    invalidateCartCache();
    if (data.status) {
      console.warn('[tote-meat-prerequisite] Could not update tote line:', data.description || data.message);
      return false;
    }
    return true;
  }

  async function removeOrphanTotes() {
    const config = getConfig();
    if (!isEnabled(config)) return false;

    const cart = await fetchCart(true);
    if (cartHasMeat(cart, config)) return false;

    const toteItems = (cart.items || []).filter((item) => isToteItem(item, config));
    if (!toteItems.length) return false;

    for (const item of toteItems) {
      await changeLineQuantity(item.key, 0);
    }
    return true;
  }

  function showFormError(form, message) {
    const productForm = form.closest('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage(message);
      return;
    }

    let wrapper = form.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper';
      wrapper.setAttribute('role', 'alert');
      wrapper.innerHTML = '<span class="product-form__error-message"></span>';
      form.prepend(wrapper);
    }
    const messageEl = wrapper.querySelector('.product-form__error-message');
    if (messageEl) messageEl.textContent = message;
    wrapper.hidden = false;
  }

  async function updateToteProductUi() {
    const config = getConfig();
    if (!isEnabled(config)) return;

    const roots = document.querySelectorAll('[data-tote-meat-prerequisite]');
    if (!roots.length) return;

    let hasMeat = false;
    try {
      const cart = await fetchCart();
      hasMeat = cartHasMeat(cart, config);
    } catch (error) {
      console.error('[tote-meat-prerequisite]', error);
      return;
    }

    roots.forEach((root) => {
      const notice = root.querySelector('[data-tote-prerequisite-notice]');
      const submit = root.querySelector('[type="submit"][name="add"], .product-form__submit');
      const dynamicCheckout = root.querySelector('.product-form__dynamic-checkout');

      if (notice) notice.hidden = hasMeat;

      if (submit && !submit.classList.contains('sold-out') && submit.dataset.toteSoldOut !== 'true') {
        if (hasMeat) {
          if (submit.dataset.toteBlocked === 'true') {
            submit.removeAttribute('disabled');
            delete submit.dataset.toteBlocked;
          }
        } else {
          submit.setAttribute('disabled', 'disabled');
          submit.dataset.toteBlocked = 'true';
        }
      }

      if (dynamicCheckout) {
        dynamicCheckout.classList.toggle('tote-prerequisite-disabled', !hasMeat);
        dynamicCheckout.style.pointerEvents = hasMeat ? '' : 'none';
        dynamicCheckout.style.opacity = hasMeat ? '' : '0.45';
      }
    });
  }

  async function runSync(event) {
    if (event?.source === SOURCE) return;
    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;
    let changed = false;
    try {
      invalidateCartCache();
      changed = await removeOrphanTotes();
      await updateToteProductUi();
    } catch (error) {
      console.error('[tote-meat-prerequisite]', error);
    } finally {
      syncing = false;
    }

    if (queued) {
      queued = false;
      return runSync({ source: 'queued' });
    }

    if (changed && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
    }
  }

  function onSubmitCapture(event) {
    const config = getConfig();
    if (!isEnabled(config)) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

    const variantId = form.querySelector('[name="id"]')?.value;
    if (!variantId || !isToteVariantId(variantId, config)) return;

    // Synchronous gate using cached cart when possible; otherwise block and check.
    event.preventDefault();
    event.stopImmediatePropagation();

    fetchCart(true)
      .then((cart) => {
        if (cartHasMeat(cart, config)) {
          document.removeEventListener('submit', onSubmitCapture, true);
          try {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit();
            } else {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          } finally {
            setTimeout(() => {
              document.addEventListener('submit', onSubmitCapture, true);
            }, 0);
          }
          return;
        }

        showFormError(form, prerequisiteMessage(config));
        const productForm = form.closest('product-form');
        productForm?.querySelector('.loading__spinner')?.classList.add('hidden');
        productForm?.querySelector('[type="submit"]')?.classList.remove('loading');
        productForm?.querySelector('[type="submit"]')?.removeAttribute('aria-disabled');
      })
      .catch((error) => {
        console.error('[tote-meat-prerequisite]', error);
        showFormError(form, prerequisiteMessage(config));
      });
  }

  function init() {
    const config = getConfig();
    if (!isEnabled(config)) return;

    document.addEventListener('submit', onSubmitCapture, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => runSync(event));
    }

    runSync({ source: 'init' });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
